import { useState } from 'react';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader } from '../components/common';
import {
  Box,
  Button,
  Typography,
  Chip,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Snackbar,
  InputAdornment,
  OutlinedInput,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add, Edit, Delete, Search, PersonAdd, Security, AdminPanelSettings, MoreVert, Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useUsers, useRoles, useAllPermissions, usePermission } from '../hooks';
import { PermissionPicker } from '../components/common/PermissionPicker';
import { colors } from '../theme/theme';

export const UsersPage = () => {
  const { can } = usePermission();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' });
  const [successDialog, setSuccessDialog] = useState({ open: false, email: '' });
  const [actionMenu, setActionMenu] = useState<{ anchorEl: HTMLElement | null; row: any }>({ anchorEl: null, row: null });
  const [permissionMode, setPermissionMode] = useState<'role' | 'custom'>('role');

  const [formData, setFormData] = useState({
    first_name: '', last_name: '', middle_name: '', email: '',
    roles: [] as number[], permissions: [] as number[],
  });

  const { users, isLoading, error, createUser, updateUser, deleteUser } = useUsers({
    page: page + 1, per_page: rowsPerPage, search,
  });
  const { roles } = useRoles({ page: 1, per_page: 100 });
  const { permissions: allPermissions } = useAllPermissions();

  // Get permissions inherited from selected roles
  const selectedRolePermissions = (() => {
    if (!roles?.data || !formData.roles.length) return [];
    const selectedRoles = roles.data.filter((r: any) => formData.roles.includes(r.id));
    const permIds = new Set<number>();
    selectedRoles.forEach((r: any) => {
      r.permissions?.forEach((p: any) => permIds.add(p.id));
    });
    return Array.from(permIds);
  })();

  const handleOpenDialog = (user?: any) => {
    if (user) {
      setSelectedUser(user);
      const userRoles = user.roles?.map((r: any) => r.id) || [];
      const userPerms = user.permissions?.map((p: any) => p.id) || [];
      setFormData({
        first_name: user.first_name || user.name?.split(' ')[0] || '',
        last_name: user.last_name || user.name?.split(' ').slice(1).join(' ') || '',
        middle_name: user.middle_name || '',
        email: user.email || '',
        roles: userRoles,
        permissions: userPerms,
      });
      // If user has direct permissions but no roles, they're custom
      setPermissionMode(userRoles.length > 0 ? 'role' : (userPerms.length > 0 ? 'custom' : 'role'));
    } else {
      setSelectedUser(null);
      setFormData({ first_name: '', last_name: '', middle_name: '', email: '', roles: [], permissions: [] });
      setPermissionMode('role');
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
  };

  const handleSave = async () => {
    try {
      if (selectedUser) {
        await updateUser({ id: selectedUser.id, data: formData as any });
        setSnackbar({ open: true, message: 'User updated successfully', severity: 'success' });
      } else {
        await createUser(formData as any);
        setSuccessDialog({ open: true, email: formData.email });
      }
      handleCloseDialog();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.errors
        ? JSON.stringify(err.response.data.errors)
        : 'Failed to save user';
      setSnackbar({ open: true, message: typeof msg === 'string' ? msg : JSON.stringify(msg), severity: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await deleteUser(id);
      setSnackbar({ open: true, message: 'User deleted successfully', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete user', severity: 'error' });
    }
  };


  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 180, display: 'flex',
      renderCell: (params) => <Typography variant="body2" fontWeight={600}>{params.value || `${params.row.first_name} ${params.row.last_name}`}</Typography>,
    },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 200, display: 'flex' },
    { field: 'roles', headerName: 'Roles', width: 200, display: 'flex',
      renderCell: (params) => (
        <Box display="flex" gap={0.5} flexWrap="wrap">
          {params.value?.map((r: any) => <Chip key={r.id} label={r.name} size="small" sx={{ textTransform: 'capitalize' }} />) || '-'}
        </Box>
      ),
    },
    { field: 'is_active', headerName: 'Status', width: 90, display: 'flex',
      renderCell: (params) => (
        <Chip label={params.value || params.row.status === 'active' ? 'Active' : 'Inactive'}
          color={params.value || params.row.status === 'active' ? 'success' : 'default'} size="small" />
      ),
    },
    { field: 'last_login_at', headerName: 'Last Login', width: 140, display: 'flex',
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
          {params.value ? new Date(params.value).toLocaleDateString() : 'Never'}
        </Typography>
      ),
    },
    { field: 'actions', headerName: '', width: 50, sortable: false, display: 'flex',
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => setActionMenu({ anchorEl: e.currentTarget, row: params.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <PageHeader title="Users" subtitle="Manage county admin users"
          breadcrumbs={[{ label: 'Dashboard', path: '/dashboard' }, { label: 'Users' }]}
          action={can('users.create') ? <Button variant="contained" startIcon={<PersonAdd />} onClick={() => handleOpenDialog()}>Add User</Button> : undefined}
        />

        {error && <Alert severity="error" sx={{ mb: 3 }}>Failed to load users.</Alert>}

        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 2, pb: 1 }}>
              <TextField fullWidth size="small" placeholder="Search users by name or email..."
                value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search /></InputAdornment> } }}
              />
            </Box>
            <Box sx={{ height: 600, width: '100%' }}>
              <DataGrid rows={users?.data || []} columns={columns} loading={isLoading}
                paginationModel={{ page, pageSize: rowsPerPage }}
                onPaginationModelChange={(m) => { setPage(m.page); setRowsPerPage(m.pageSize); }}
                pageSizeOptions={[10, 25, 50]} rowCount={users?.meta?.total || users?.total || 0}
                paginationMode="server" disableRowSelectionOnClick
                sx={{ border: 'none',
                  '& .MuiDataGrid-columnHeaders': { backgroundColor: colors.grey[50], borderBottom: `2px solid ${colors.grey[300]}` },
                  '& .MuiDataGrid-cell': { borderBottom: `1px solid ${colors.grey[200]}` },
                }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Actions Menu */}
        <Menu anchorEl={actionMenu.anchorEl} open={Boolean(actionMenu.anchorEl)}
          onClose={() => setActionMenu({ anchorEl: null, row: null })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          {can('users.view') && (
            <MenuItem onClick={() => {
              navigate(`/users/${actionMenu.row?.id}`);
              setActionMenu({ anchorEl: null, row: null });
            }}>
              <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
              <ListItemText>View Profile</ListItemText>
            </MenuItem>
          )}
          {can('users.edit') && (
            <MenuItem onClick={() => {
              handleOpenDialog(actionMenu.row);
              setActionMenu({ anchorEl: null, row: null });
            }}>
              <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
              <ListItemText>Edit User</ListItemText>
            </MenuItem>
          )}
          {can('users.delete') && (
            <MenuItem onClick={() => {
              handleDelete(actionMenu.row?.id);
              setActionMenu({ anchorEl: null, row: null });
            }}>
              <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Delete User</ListItemText>
            </MenuItem>
          )}
        </Menu>

        {/* Add/Edit User Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>{selectedUser ? 'Edit User' : 'Add New User'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {!selectedUser && (
                <Alert severity="info">
                  A secure password will be auto-generated. The user will be required to change it on first login.
                </Alert>
              )}

              <Typography variant="overline" color="text.secondary">Basic Information</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField fullWidth required label="First Name" value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} />
                <TextField fullWidth label="Middle Name" value={formData.middle_name}
                  onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })} />
                <TextField fullWidth required label="Last Name" value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} />
              </Box>
              <TextField fullWidth required label="Email" type="email" value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={!!selectedUser} />

              <Divider sx={{ my: 1 }}><Typography variant="caption" color="text.secondary">Access Control</Typography></Divider>

              {/* Mode Toggle */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <ToggleButtonGroup value={permissionMode} exclusive
                  onChange={(_, v) => {
                    if (!v) return;
                    setPermissionMode(v);
                    if (v === 'role') {
                      setFormData(prev => ({ ...prev, permissions: [] }));
                    } else {
                      setFormData(prev => ({ ...prev, roles: [] }));
                    }
                  }} size="small">
                  <ToggleButton value="role">
                    <AdminPanelSettings sx={{ mr: 0.5, fontSize: 18 }} /> Assign Role
                  </ToggleButton>
                  <ToggleButton value="custom">
                    <Security sx={{ mr: 0.5, fontSize: 18 }} /> Custom Permissions
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {permissionMode === 'role' ? (
                <>
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Select a role to assign predefined permissions to this user. The user will inherit all permissions associated with the selected role(s).
                  </Alert>
                  <FormControl fullWidth>
                    <InputLabel>Role</InputLabel>
                    <Select value={formData.roles[0] || ''} label="Role"
                      onChange={(e) => setFormData({ ...formData, roles: e.target.value ? [e.target.value as number] : [] })}>
                      <MenuItem value=""><em>Select a role</em></MenuItem>
                      {roles?.data?.map((role: any) => (
                        <MenuItem key={role.id} value={role.id}>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{role.name}</Typography>
                            {role.description && <Typography variant="caption" color="text.secondary" display="block">{role.description}</Typography>}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Show inherited permissions for selected roles */}
                  {selectedRolePermissions.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Inherited Permissions ({selectedRolePermissions.length})
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {allPermissions?.filter((p: any) => selectedRolePermissions.includes(p.id)).map((p: any) => (
                          <Chip key={p.id} label={p.name.replace(/_/g, ' ')} size="small" variant="outlined"
                            sx={{ fontSize: '0.7rem', textTransform: 'capitalize' }} />
                        ))}
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <>
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Select specific permissions for this user. This gives you fine-grained control over what the user can access.
                  </Alert>
                  <PermissionPicker
                    permissions={allPermissions || []}
                    selected={formData.permissions}
                    onChange={(selected) => setFormData({ ...formData, permissions: selected })}
                  />
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleSave}
              disabled={!formData.first_name || !formData.last_name || !formData.email}>
              {selectedUser ? 'Update User' : 'Create User'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* User Created Success Dialog */}
        <Dialog open={successDialog.open}
          onClose={() => setSuccessDialog({ open: false, email: '' })} maxWidth="sm" fullWidth>
          <DialogTitle>User Created Successfully</DialogTitle>
          <DialogContent>
            <Alert severity="success" sx={{ mb: 3 }}>
              The user account has been created and login credentials have been sent to their email.
            </Alert>
            <Box sx={{ p: 2, bgcolor: colors.grey[50], borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">Credentials sent to</Typography>
              <Typography variant="body1" fontWeight={600}>{successDialog.email}</Typography>
            </Box>
            <Alert severity="info" sx={{ mt: 2 }}>
              The user will be required to change their password upon first login.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={() => setSuccessDialog({ open: false, email: '' })}>
              Done
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminLayout>
  );
};
